import { ApiResult, HttpClient } from "../http.js";
import { PriceLibraryCategoryDto, PriceLibraryItemDto } from "../types/price-library.js";

export interface CreatePriceLibraryItemPayload {
  categoryId: string;
  categoryName: string;
  sortOrder: number;
  name: string;
  description?: string | null;
  unit: string;
  hoursPerUnit: number;
  materialsPerUnit: number;
  equipmentPerUnit: number;
  subcontractorPerUnit: number;
  salesPricePerUnit?: number | null;
  isArchived?: boolean;
}

export type UpdatePriceLibraryItemPayload = CreatePriceLibraryItemPayload;

/** One material line in a whole-set replacement (`PUT /api/price-library/{id}/material-lines`). */
export interface PriceLibraryMaterialLinePayload {
  materialName: string;
  supplierPriceItemId?: string | null;
  /** Must be > 0. */
  consumptionPerUnit: number;
  consumptionUnit: string;
  /** 0-100, default 0. */
  wastePercent?: number;
  /** Must be > 0 when given. */
  packSize?: number | null;
  packUnit?: string | null;
  purchasePricePerPack?: number | null;
}

export interface ReplacePriceLibraryMaterialLinesPayload {
  lines: PriceLibraryMaterialLinePayload[];
}

export interface ListPriceLibraryItemsQuery {
  /** 1-based. Omit for the full (unpaged) list — identical to the pre-paging response. */
  page?: number;
  pageSize?: number;
  /** Matched server-side against Name, Description OR category name. */
  search?: string;
  categoryId?: string;
  /** Omit for archived and active items alike (the pre-filter behaviour). */
  isArchived?: boolean;
}

/**
 * Company-scoped price library (reusable construction work items with standard
 * per-unit rates). Replaces the legacy Firestore `PRICE_LIBRARY_ITEMS` collection.
 */
export class PriceLibraryApi {
  constructor(private readonly http: HttpClient) {}

  /** Response stays a flat array whether or not `query` is given — see `ListPriceLibraryItemsQuery`. */
  list(query: ListPriceLibraryItemsQuery = {}): Promise<ApiResult<PriceLibraryItemDto[]>> {
    return this.http.get<PriceLibraryItemDto[]>("/api/price-library", { query: { ...query } });
  }

  /** The distinct categories of the org's non-archived items, with item counts. */
  categories(): Promise<ApiResult<PriceLibraryCategoryDto[]>> {
    return this.http.get<PriceLibraryCategoryDto[]>("/api/price-library/categories");
  }

  get(id: string): Promise<ApiResult<PriceLibraryItemDto>> {
    return this.http.get<PriceLibraryItemDto>(`/api/price-library/${encodeURIComponent(id)}`);
  }

  create(payload: CreatePriceLibraryItemPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/price-library", { body: payload });
  }

  update(id: string, payload: UpdatePriceLibraryItemPayload): Promise<ApiResult<PriceLibraryItemDto>> {
    return this.http.put<PriceLibraryItemDto>(`/api/price-library/${encodeURIComponent(id)}`, { body: payload });
  }

  /** Replaces ALL material (consumption) lines of the item; an empty array clears them. Needs `priceLibrary.edit`. */
  replaceMaterialLines(
    id: string,
    payload: ReplacePriceLibraryMaterialLinesPayload,
  ): Promise<ApiResult<PriceLibraryItemDto>> {
    return this.http.put<PriceLibraryItemDto>(`/api/price-library/${encodeURIComponent(id)}/material-lines`, {
      body: payload,
    });
  }

  remove(id: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(`/api/price-library/${encodeURIComponent(id)}`);
  }
}
